import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { userAPI, designationAPI } from '../utils/api';
import { Plus, Users, Shield, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'case_officer', label: 'Case Officer' },
  { value: 'process_manager', label: 'Process Manager' },
  { value: 'sales_consultant', label: 'Sales Consultant' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'marketing_manager', label: 'Marketing Manager' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'super_admin', label: 'Super Admin' },
];

export default function AdminStaff() {
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'case_officer',
    phone: '',
    designation: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchDesignations();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getUsers();
      setUsers(res.data.filter(u => u.role !== 'client'));
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const res = await designationAPI.getDesignations();
      setDesignations(res.data || []);
    } catch {
      // non-critical
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.designation) delete payload.designation;
      await userAPI.createUser(payload);
      toast.success('Staff member added successfully!');
      setShowForm(false);
      fetchUsers();
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'case_officer',
        phone: '',
        designation: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <Shield size={20} className="text-red-500" />;
      case 'process_manager':
        return <UserCog size={20} className="text-blue-500" />;
      case 'case_officer':
        return <Users size={20} className="text-green-500" />;
      case 'sales_consultant':
      case 'sales_manager':
        return <Users size={20} className="text-violet-500" />;
      case 'marketing_manager':
        return <Users size={20} className="text-pink-500" />;
      case 'accountant':
        return <Users size={20} className="text-amber-500" />;
      default:
        return <Users size={20} className="text-slate-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'status-rejected';
      case 'process_manager':
        return 'status-active';
      case 'case_officer':
        return 'status-approved';
      case 'sales_consultant':
      case 'sales_manager':
        return 'status-pending';
      case 'marketing_manager':
        return 'status-rejected';
      case 'accountant':
        return 'status-active';
      default:
        return 'status-pending';
    }
  };

  const getRoleLabel = (role) => {
    return ROLES.find(r => r.value === role)?.label || role.replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="admin-staff-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="staff-title">Staff Management</h1>
            <p className="text-slate-600">Manage your team members</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="add-staff-button"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Staff
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="staff-form">
            <h2 className="text-xl font-bold text-slate-900 mb-4">New Staff Member</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    data-testid="input-full-name"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="input-email"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    data-testid="input-password"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    data-testid="input-phone"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  data-testid="select-role"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Designation</label>
                {designations.length > 0 ? (
                  <select
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="">— None —</option>
                    {designations.map(d => (
                      <option key={d.id || d._id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    placeholder="e.g. Senior Case Officer"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                )}
              </div>
              <div className="flex gap-4">
                <button type="submit" data-testid="submit-staff-button" className="btn-primary">
                  Add Staff Member
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  data-testid="cancel-button"
                  className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="staff-grid">
          {users.length === 0 ? (
            <div className="col-span-full text-center py-16">
              <Users size={64} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No staff members yet. Add your first staff member to get started.</p>
            </div>
          ) : (
            users.map((user) => (
              <div key={user.id} className="stat-card" data-testid={`staff-card-${user.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-100 p-3 rounded-full">
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{user.full_name}</h3>
                      {user.designation && (
                        <p className="text-xs font-medium text-indigo-600">{user.designation}</p>
                      )}
                      <p className="text-sm text-slate-600">{user.email}</p>
                    </div>
                  </div>
                </div>
                <span className={`status-badge ${getRoleBadgeColor(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
                {user.phone && (
                  <p className="text-sm text-slate-600 mt-2">{user.phone}</p>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500">Joined {new Date(user.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
